import { z } from 'zod';

import { BREAKDOWN_PANEL_CATEGORIES, ElementCategory } from './element.js';
import { EvidenceSpan } from './production-plan.js';

/**
 * THE SCENE BREAKDOWN WORKSPACE — the per-scene review of what a scene needs (Production
 * Workspace 2.0, 2026-11-05; design record
 * `docs/superpowers/specs/2026-11-04-workspace-scene-breakdown-design.md`).
 *
 * Everything here is a vocabulary or a pure rule that the server applies and the SPA renders.
 * Nothing is derived twice: progress, coverage, item state and counts are computed on the server
 * with these functions, and the SPA draws what it is given (rule 1 of the design record).
 */

// ---------------------------------------------------------------------------------------------
// Items and their per-scene state machine (owner correction C2)
// ---------------------------------------------------------------------------------------------

/**
 * Where an item on a scene's sheet comes from. Shown on every item, so an AI reading never
 * reads as a fact a person stated.
 *
 * - `AI_READING`: the model read it in this scene (an `element_mention` from a model call).
 * - `CHARACTER_CUE`: a character cue in the screenplay, a fact of the format (`structural-parse-1`).
 * - `ADDED_BY_HAND`: a person put it on this scene; no reading proposed it here.
 */
export const ItemOrigin = z.enum(['AI_READING', 'CHARACTER_CUE', 'ADDED_BY_HAND']);
export type ItemOrigin = z.infer<typeof ItemOrigin>;

/**
 * An item's state IN ONE SCENE. The same canonical element can be on the sheet in scene 3 and
 * not needed in scene 5; nothing a person does in one scene changes another.
 *
 * - `ON_SHEET`: a live `production_element_scene` link exists for this scene.
 * - `NOT_NEEDED`: the latest decision on this scene's mention is REJECT (removing an AI-read item
 *   from the scene records one).
 * - `AWAITING`: a reading proposed it here and nobody has decided.
 */
export const ItemState = z.enum(['AWAITING', 'ON_SHEET', 'NOT_NEEDED']);
export type ItemState = z.infer<typeof ItemState>;

/** The three per-scene verbs. EDIT (rename) changes a shared field and is not a state move. */
export const ItemVerb = z.enum(['ACCEPT', 'REJECT', 'RESTORE']);
export type ItemVerb = z.infer<typeof ItemVerb>;

export type ItemTransition =
  | { readonly kind: 'MOVE'; readonly to: ItemState }
  | { readonly kind: 'NO_OP' }
  | { readonly kind: 'NOT_APPLICABLE' };

/**
 * THE TRANSITION TABLE, as data: the service applies it and the tests walk every cell.
 *
 * A repeated verb is a no-op that answers success (201, the POST default) and writes no decision, so a double click or a
 * retried request is harmless. RESTORE undoes a "not needed"; on an item that is on the sheet
 * there is nothing to restore, and it is refused (409 `DECISION_NOT_APPLICABLE`) rather than
 * quietly taking the item off.
 */
export const ITEM_TRANSITIONS: Readonly<Record<ItemState, Readonly<Record<ItemVerb, ItemTransition>>>> = {
  AWAITING: {
    ACCEPT: { kind: 'MOVE', to: 'ON_SHEET' },
    REJECT: { kind: 'MOVE', to: 'NOT_NEEDED' },
    RESTORE: { kind: 'NO_OP' },
  },
  ON_SHEET: {
    ACCEPT: { kind: 'NO_OP' },
    REJECT: { kind: 'MOVE', to: 'NOT_NEEDED' },
    RESTORE: { kind: 'NOT_APPLICABLE' },
  },
  NOT_NEEDED: {
    ACCEPT: { kind: 'MOVE', to: 'ON_SHEET' },
    REJECT: { kind: 'NO_OP' },
    RESTORE: { kind: 'MOVE', to: 'AWAITING' },
  },
};

export function itemTransition(from: ItemState, verb: ItemVerb): ItemTransition {
  return ITEM_TRANSITIONS[from][verb];
}

/**
 * The state of one (scene, item), from the rows that hold it. The ONE derivation: the decide
 * service reads the current state through it before applying the table, and the read API through
 * it before answering.
 */
export function itemStateOf(input: {
  /** A live `production_element_scene` row for this scene. */
  readonly linked: boolean;
  /** The newest decision on this scene's mention, if a mention exists. */
  readonly latestDecision: string | null;
}): ItemState {
  if (input.linked) return 'ON_SHEET';
  if (input.latestDecision === 'REJECT') return 'NOT_NEEDED';
  return 'AWAITING';
}

/** What `POST …/mentions/:id/decide` answers for a per-scene verb. */
export const ItemDecisionResult = z.object({
  state: ItemState,
  /** False for a no-op: nothing was written. */
  changed: z.boolean(),
  productionElementId: z.string().nullable(),
  rowVersion: z.number().int(),
});
export type ItemDecisionResult = z.infer<typeof ItemDecisionResult>;

/**
 * A hand-added name that matches something already in the production but NOT read in this scene
 * (owner safeguard S1). Two swords are not one sword because both are called "sword": the server
 * refuses to merge silently (409 `ELEMENT_IDENTITY_UNCONFIRMED`) and names the candidates, and the
 * person answers `sameAs` or `distinct`.
 */
export const IdentityCandidate = z.object({
  /**
   * What `sameAs` sends back, echoed as given: a production element's id, or `el:<elementId>` for
   * a canonical element of the screenplay that nobody has accepted yet (so it has no production
   * element to name).
   */
  productionElementId: z.string(),
  name: z.string(),
  category: ElementCategory,
  sceneNumbers: z.array(z.string()),
});
export type IdentityCandidate = z.infer<typeof IdentityCandidate>;

export const ELEMENT_IDENTITY_UNCONFIRMED = 'ELEMENT_IDENTITY_UNCONFIRMED';

// ---------------------------------------------------------------------------------------------
// Coverage: what the AI reading actually examined (C1, S2)
// ---------------------------------------------------------------------------------------------

/**
 * What a run recorded about one category in one scene.
 *
 * - `CHECKED`: EVERY part of the scene the category needed was answered.
 * - `INCOMPLETE`: some parts were answered and some were not; the partial results are kept.
 * - `FAILED`: asked, and no part was answered.
 * - `SKIPPED`: the skill router did not select the category, so nothing asked.
 * - `UNREAD`: the scene was not read at all (left out, or did not fit).
 */
export const StoredCoverageStatus = z.enum(['CHECKED', 'INCOMPLETE', 'FAILED', 'SKIPPED', 'UNREAD']);
export type StoredCoverageStatus = z.infer<typeof StoredCoverageStatus>;

/** As shown: a stored status, or one of the two derived at read time. */
export const CategoryCoverage = z.enum([
  'CHECKED',
  'INCOMPLETE',
  'FAILED',
  'SKIPPED',
  'UNREAD',
  /** Read before the scene's text changed (`content_changed_at` after the category's read). */
  'OUT_OF_DATE',
  /** Read by a run before coverage was recorded: nothing is known per category. */
  'NOT_RECORDED',
  /** No reading has ever run on the scene. */
  'NOT_ANALYSED',
]);
export type CategoryCoverage = z.infer<typeof CategoryCoverage>;

export const ElementEngineName = z.enum(['departments', 'skills']);
export type ElementEngineName = z.infer<typeof ElementEngineName>;

const CoverageEntry = z.object({
  status: StoredCoverageStatus,
  runId: z.string(),
  /** ISO time the run STARTED reading, comparable with `content_changed_at`. */
  at: z.string(),
  engine: ElementEngineName,
});

/**
 * `script_breakdown_scene.element_coverage`. Per category, the reading whose RESULTS are shown,
 * plus the newest attempt when a later one did worse (S2): a failed re-read never overwrites a
 * category that was checked, it is recorded beside it.
 */
export const StoredElementCoverage = z.object({
  version: z.literal(1),
  categories: z.partialRecord(ElementCategory, CoverageEntry),
  lastAttempt: z.partialRecord(ElementCategory, CoverageEntry).optional(),
});
export type StoredElementCoverage = z.infer<typeof StoredElementCoverage>;
export type StoredCoverageEntry = z.infer<typeof CoverageEntry>;

/** A stored status replaces the shown one only when it is at least as informative. */
const COVERAGE_STRENGTH: Readonly<Record<StoredCoverageStatus, number>> = {
  CHECKED: 4,
  INCOMPLETE: 3,
  SKIPPED: 2,
  FAILED: 1,
  UNREAD: 0,
};

/**
 * Merge one run's per-category outcome into a scene's stored coverage (S2).
 *
 * The run's status becomes the shown one when the run's results replace what is shown for the
 * category: a CHECKED or INCOMPLETE answer always does (a new reading's proposals are the current
 * ones), and so does any outcome over a category that showed nothing better. A FAILED, UNREAD or
 * SKIPPED attempt over a CHECKED or INCOMPLETE category keeps the earlier one and is recorded as
 * `lastAttempt`, so the screen can say "a later reading failed; showing the earlier one" instead
 * of "not read".
 */
export function mergeCoverage(
  stored: StoredElementCoverage | null,
  run: { readonly runId: string; readonly at: string; readonly engine: ElementEngineName },
  outcomes: Partial<Record<ElementCategory, StoredCoverageStatus>>,
): StoredElementCoverage {
  const categories: Partial<Record<ElementCategory, StoredCoverageEntry>> = { ...(stored?.categories ?? {}) };
  const lastAttempt: Partial<Record<ElementCategory, StoredCoverageEntry>> = { ...(stored?.lastAttempt ?? {}) };

  for (const [category, status] of Object.entries(outcomes) as [ElementCategory, StoredCoverageStatus][]) {
    const entry: StoredCoverageEntry = { status, runId: run.runId, at: run.at, engine: run.engine };
    const shown = categories[category];
    const replaces =
      shown === undefined ||
      status === 'CHECKED' ||
      status === 'INCOMPLETE' ||
      COVERAGE_STRENGTH[status] >= COVERAGE_STRENGTH[shown.status];

    if (replaces) {
      categories[category] = entry;
      Reflect.deleteProperty(lastAttempt, category);
    } else {
      lastAttempt[category] = entry;
    }
  }

  return Object.keys(lastAttempt).length === 0
    ? { version: 1, categories }
    : { version: 1, categories, lastAttempt };
}

/** The per-category coverage as shown, every panel category present. */
export function categoryCoverageOf(input: {
  readonly stored: StoredElementCoverage | null;
  /** `scene.analysed_at`: any reading at all, before or after coverage was recorded. */
  readonly analysedAt: Date | string | null;
  readonly contentChangedAt: Date | string | null;
}): Record<ElementCategory, CategoryCoverage> {
  const changed = input.contentChangedAt === null ? null : new Date(input.contentChangedAt).getTime();
  const out = {} as Record<ElementCategory, CategoryCoverage>;

  for (const category of ElementCategory.options) {
    const entry = input.stored?.categories[category];
    if (entry === undefined) {
      out[category] = input.analysedAt === null ? 'NOT_ANALYSED' : 'NOT_RECORDED';
      continue;
    }
    const readAt = new Date(entry.at).getTime();
    const outOfDate = changed !== null && Number.isFinite(readAt) && changed > readAt && entry.status !== 'UNREAD';
    out[category] = outOfDate ? 'OUT_OF_DATE' : entry.status;
  }

  return out;
}

/**
 * A scene's AI coverage in one word, over the breakdown panel's categories. Shown APART from the
 * human review progress and never merged with it: a scene a person reviewed may still have been
 * read only in part.
 *
 * - `FULL`: every category checked.
 * - `SELECTIVE`: every category the reading chose to inspect was checked; the rest were skipped.
 * - `PARTIAL`: some category incomplete, failed or unread while others were read.
 * - `NOT_READ`, `OUT_OF_DATE`, `NOT_RECORDED`, `NOT_ANALYSED`: as for a category.
 */
export const SceneCoverage = z.enum([
  'FULL',
  'SELECTIVE',
  'PARTIAL',
  'NOT_READ',
  'OUT_OF_DATE',
  'NOT_RECORDED',
  'NOT_ANALYSED',
]);
export type SceneCoverage = z.infer<typeof SceneCoverage>;

export function sceneCoverageOf(categories: Readonly<Record<ElementCategory, CategoryCoverage>>): SceneCoverage {
  const shown = BREAKDOWN_PANEL_CATEGORIES.map((c) => categories[c]);

  if (shown.every((s) => s === 'NOT_ANALYSED')) return 'NOT_ANALYSED';
  if (shown.every((s) => s === 'NOT_RECORDED' || s === 'NOT_ANALYSED')) return 'NOT_RECORDED';
  if (shown.some((s) => s === 'OUT_OF_DATE')) return 'OUT_OF_DATE';

  const read = shown.filter((s) => s === 'CHECKED' || s === 'INCOMPLETE').length;
  if (read === 0) return 'NOT_READ';
  if (shown.every((s) => s === 'CHECKED')) return 'FULL';
  if (shown.every((s) => s === 'CHECKED' || s === 'SKIPPED')) return 'SELECTIVE';
  return 'PARTIAL';
}

// ---------------------------------------------------------------------------------------------
// Human review progress, with history (C5)
// ---------------------------------------------------------------------------------------------

/** One row of `script_breakdown_scene_review_event`: what happened to a scene's review. */
export const ReviewEventAction = z.enum(['REVIEWED', 'WITHDRAWN', 'INVALIDATED']);
export type ReviewEventAction = z.infer<typeof ReviewEventAction>;

/**
 * - `NOT_STARTED`: never completed, nothing decided.
 * - `IN_PROGRESS`: never completed (or started over), something decided.
 * - `REVIEWED`: completed.
 * - `NEEDS_RE_REVIEW`: completed, then a change invalidated it (`reason` says which).
 * - `REOPENED`: completed, then a person took it back out of reviewed.
 */
export const SceneReviewProgress = z.enum(['NOT_STARTED', 'IN_PROGRESS', 'REVIEWED', 'NEEDS_RE_REVIEW', 'REOPENED']);
export type SceneReviewProgress = z.infer<typeof SceneReviewProgress>;

/**
 * Progress from the scene row and its newest review event — never from `IN_REVIEW` alone.
 *
 * Rows from before the history existed have no event; for them the row itself still tells the
 * two apart, because the two writers left different marks: `unmarkReviewed` clears
 * `breakdown_reviewed_at`, `ReviewInvalidationService.invalidateScenes` keeps it.
 */
export function reviewProgressOf(input: {
  readonly reviewStatus: string;
  readonly reviewedAt: Date | string | null;
  readonly latestEvent: { readonly action: ReviewEventAction; readonly reason: string | null } | null;
  /** Any decision on this scene's mentions, or any item a person put on it. */
  readonly hasWork: boolean;
}): { progress: SceneReviewProgress; reason: string | null } {
  if (input.reviewStatus === 'REVIEWED') return { progress: 'REVIEWED', reason: null };

  if (input.reviewStatus === 'IN_REVIEW') {
    const event = input.latestEvent;
    if (event?.action === 'INVALIDATED') return { progress: 'NEEDS_RE_REVIEW', reason: event.reason };
    if (event?.action === 'WITHDRAWN') return { progress: 'REOPENED', reason: null };
    // No event that explains it: a row from before the history.
    return input.reviewedAt === null
      ? { progress: 'REOPENED', reason: null }
      : { progress: 'NEEDS_RE_REVIEW', reason: null };
  }

  return { progress: input.hasWork ? 'IN_PROGRESS' : 'NOT_STARTED', reason: null };
}

// ---------------------------------------------------------------------------------------------
// Evidence as shown (C4)
// ---------------------------------------------------------------------------------------------

/**
 * How an item's place in the screenplay is known. The label says what was verified, and only
 * `VERIFIED` and `HEADING` are verified source evidence.
 *
 * - `VERIFIED`: an exact stored span in the scene's text (`EXACT`).
 * - `HEADING`: an exact stored span in the scene heading (a location, a vehicle, the time of day).
 * - `RELOCATED`: found once, but not where the reading said; shown dashed, to check.
 * - `AMBIGUOUS`: the quote appears more than once; every occurrence is marked, none is chosen.
 * - `APPROXIMATE`: no stored evidence; the name appears in the text. Never verified.
 * - `NONE`: nothing located.
 */
export const EvidenceMatch = z.enum(['VERIFIED', 'HEADING', 'RELOCATED', 'AMBIGUOUS', 'APPROXIMATE', 'NONE']);
export type EvidenceMatch = z.infer<typeof EvidenceMatch>;

export const TextRange = z.object({ start: z.number().int(), end: z.number().int() });
export type TextRange = z.infer<typeof TextRange>;

export const ItemEvidence = z.object({
  match: EvidenceMatch,
  /** The stored evidence (the production plan's span shape), scene-relative; null without one. */
  span: EvidenceSpan.nullable(),
  /** Where the ranges point: the scene text, or the heading line. */
  target: z.enum(['TEXT', 'HEADING']),
  /** Every range to mark: one for VERIFIED/HEADING/RELOCATED, each occurrence otherwise. */
  ranges: z.array(TextRange),
});
export type ItemEvidence = z.infer<typeof ItemEvidence>;

// ---------------------------------------------------------------------------------------------
// The read API: a light index of every scene, and one scene in full (C8)
// ---------------------------------------------------------------------------------------------

/** A stable key per item in a scene: the production element once there is one, else the canonical element. */
export function itemKeyOf(input: { readonly productionElementId: string | null; readonly elementId: string | null }): string {
  if (input.productionElementId !== null) return `pe:${input.productionElementId}`;
  if (input.elementId !== null) return `el:${input.elementId}`;
  throw new Error('An item needs a production element or a canonical element');
}

export const ItemCounts = z.object({
  /** Distinct items that are not "not needed". */
  items: z.number().int(),
  awaiting: z.number().int(),
  byCategory: z.partialRecord(ElementCategory, z.number().int()),
});
export type ItemCounts = z.infer<typeof ItemCounts>;

export const ReviewSummary = z.object({
  scenes: z.number().int(),
  notStarted: z.number().int(),
  inProgress: z.number().int(),
  reviewed: z.number().int(),
  needsReReview: z.number().int(),
  reopened: z.number().int(),
  /** Scenes whose AI coverage is not FULL or SELECTIVE: a separate fact from review progress. */
  notFullyRead: z.number().int(),
});
export type ReviewSummary = z.infer<typeof ReviewSummary>;

export const TakeawayIndexState = z.enum(['NONE', 'READ', 'READ_EMPTY', 'UNREAD', 'FAILED', 'UNAVAILABLE']);
export type TakeawayIndexState = z.infer<typeof TakeawayIndexState>;

export const SceneIndexRow = z.object({
  id: z.string(),
  ordinal: z.number().int(),
  sceneNumber: z.string().nullable(),
  slugline: z.string().nullable(),
  intExt: z.string().nullable(),
  timeOfDay: z.string().nullable(),
  pageEighths: z.number().int().nullable(),
  progress: SceneReviewProgress,
  progressReason: z.string().nullable(),
  coverage: SceneCoverage,
  categoryCoverage: z.record(ElementCategory, CategoryCoverage),
  counts: ItemCounts,
  castNames: z.array(z.string()),
  takeaways: z.object({ state: TakeawayIndexState, outOfDate: z.boolean() }),
});
export type SceneIndexRow = z.infer<typeof SceneIndexRow>;

export const BreakdownIndexView = z.object({
  scriptVersionId: z.string(),
  versionNumber: z.number().int().nullable(),
  summary: ReviewSummary,
  rows: z.array(SceneIndexRow),
});
export type BreakdownIndexView = z.infer<typeof BreakdownIndexView>;

export const BreakdownSearchView = z.object({ q: z.string(), sceneIds: z.array(z.string()) });
export type BreakdownSearchView = z.infer<typeof BreakdownSearchView>;

/**
 * How `GET …/breakdown-index/search` matches (`?match=`).
 *
 * - `substring` (the default): the list's search box — "harb" finds the harbour as it is typed.
 * - `word`: whole words by `appearsInText`'s rule (case-insensitive, any whitespace between the
 *   words). Tagging a phrase on "every scene that names it" asks this, because a substring writes
 *   "car" onto the scene with a scarf in it, and the producer confirms the list it is shown.
 */
export const BreakdownSearchMatch = z.enum(['substring', 'word']);
export type BreakdownSearchMatch = z.infer<typeof BreakdownSearchMatch>;

/** A scene an item's production element is on, as "Remove from every scene" lists it. */
export const ItemSceneRef = z.object({ sceneId: z.string(), sceneNumber: z.string().nullable() });
export type ItemSceneRef = z.infer<typeof ItemSceneRef>;

export const SceneItemView = z.object({
  key: z.string(),
  category: ElementCategory,
  name: z.string(),
  state: ItemState,
  origin: ItemOrigin,
  elementId: z.string().nullable(),
  productionElementId: z.string().nullable(),
  mentionId: z.string().nullable(),
  /** The production element's version, for a rename. */
  rowVersion: z.number().int().nullable(),
  /** The count a person entered for this scene. */
  quantity: z.number().int().nullable(),
  /** A count the script states, as the reading saw it: a hint, never a count. */
  quantityHint: z.number().int().nullable(),
  confidence: z.enum(['HIGH', 'NEEDS_REVIEW', 'AMBIGUOUS']).nullable(),
  evidence: ItemEvidence,
  /**
   * The live scenes of THIS draft whose sheet carries the item's production element, in script
   * order — what "Remove from every scene" lists before anything is removed. Sent by the scene
   * detail for an item with a production element only (absent otherwise, and on every other
   * read): another draft's scenes are never listed, because removing from them is not this
   * screen's to offer.
   */
  onScenes: z.array(ItemSceneRef).optional(),
});
export type SceneItemView = z.infer<typeof SceneItemView>;

export const CategoryCoverageDetail = z.object({
  category: ElementCategory,
  coverage: CategoryCoverage,
  /** A later attempt that did worse than what is shown (S2). */
  lastAttempt: z.object({ status: StoredCoverageStatus, at: z.string() }).nullable(),
});
export type CategoryCoverageDetail = z.infer<typeof CategoryCoverageDetail>;
